
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Video, Clock, AlertCircle, CheckCircle } from "lucide-react";
import { format, parseISO, isAfter, isBefore, addMinutes, subMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';

interface VideoCallButtonProps {
  appointmentId: string;
  date: string;
  time: string;
  duration: number;
  status: string;
  meetLink?: string;
  role: 'patient' | 'psychologist';
}

const VideoCallButton: React.FC<VideoCallButtonProps> = ({
  appointmentId,
  date,
  time,
  duration,
  status,
  meetLink,
  role
}) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [timeStatus, setTimeStatus] = useState<'early' | 'available' | 'late' | 'expired'>('early');
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);

  useEffect(() => {
    const checkAvailability = () => {
      if (status !== 'confirmed' || !meetLink) {
        setIsAvailable(false);
        return;
      }

      const now = new Date();
      const appointmentDateTime = parseISO(`${date}T${time}`);
      const startTime = subMinutes(appointmentDateTime, 15); // 15 min antes
      const endTime = addMinutes(appointmentDateTime, duration + 15); // duração + 15 min depois

      if (isBefore(now, startTime)) {
        setTimeStatus('early');
        setIsAvailable(false);
        const diff = startTime.getTime() - now.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        if (hours > 0) {
          setTimeRemaining(`${hours}h ${minutes % 60}min`);
        } else {
          setTimeRemaining(`${minutes}min`);
        }
      } else if (isAfter(now, endTime)) {
        setTimeStatus('expired');
        setIsAvailable(false);
        setTimeRemaining(null);
      } else if (isAfter(now, appointmentDateTime)) {
        setTimeStatus('late');
        setIsAvailable(true);
        setTimeRemaining('Consulta em andamento');
      } else {
        setTimeStatus('available');
        setIsAvailable(true);
        setTimeRemaining('Disponível para acesso');
      }
    };

    checkAvailability();
    const interval = setInterval(checkAvailability, 60000); // Verificar a cada minuto

    return () => clearInterval(interval);
  }, [date, time, duration, status, meetLink]);

  const formatDateTime = () => {
    try {
      const appointmentDateTime = parseISO(`${date}T${time}`);
      return format(appointmentDateTime, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (e) {
      return "Data inválida";
    }
  };

  const getStatusBadge = () => {
    switch (timeStatus) {
      case 'early':
        return <Badge variant="secondary" className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Aguardando
        </Badge>;
      case 'available':
        return <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Disponível
        </Badge>;
      case 'late':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Em andamento
        </Badge>;
      case 'expired':
        return <Badge variant="outline" className="text-muted-foreground">
          Expirado
        </Badge>;
      default:
        return null;
    }
  };

  if (status !== 'confirmed') {
    return (
      <div className="text-center p-4 bg-muted rounded-lg">
        <p className="text-sm text-muted-foreground">
          Videochamada disponível apenas para consultas confirmadas
        </p>
      </div>
    );
  }

  if (!meetLink) {
    return (
      <div className="text-center p-4 bg-muted rounded-lg">
        <p className="text-sm text-muted-foreground">
          Link da videochamada será gerado automaticamente
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Video className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Videochamada</span>
        </div>
        {getStatusBadge()}
      </div>
      
      <div className="text-sm text-muted-foreground">
        <p>Consulta: {formatDateTime()}</p>
        {timeRemaining && <p>{timeRemaining}</p>}
      </div>

      {isAvailable ? (
        <Button 
          asChild 
          className="w-full"
          variant={timeStatus === 'late' ? 'destructive' : 'default'}
        >
          <Link to={`/appointment/videocall/${appointmentId}`}>
            <Video className="h-4 w-4 mr-2" />
            Entrar na Consulta
          </Link>
        </Button>
      ) : (
        <Button disabled className="w-full">
          <Clock className="h-4 w-4 mr-2" />
          {timeStatus === 'early' ? 'Aguardar Horário' : 'Indisponível'}
        </Button>
      )}

      <div className="text-xs text-muted-foreground">
        <p>• Acesso liberado 15 minutos antes da consulta</p>
        <p>• Sala disponível até 15 minutos após o fim</p>
      </div>
    </div>
  );
};

export default VideoCallButton;
